/**
 * DriverProfileSummary - Personal info + availability toggle + cost breakdown
 */
import React, { useState, useEffect } from 'react';
import { User, MapPin, Mail, DollarSign, Fuel, AlertTriangle, FileWarning, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import PhoneLink from '../../../components/common/PhoneLink.jsx';
import { useFormat } from '../../../i18n/format.js';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';

const AVAILABILITY_STATUSES = [
  { value: 'AVAILABLE', label: 'Disponible', icon: CheckCircle, color: 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/20 dark:border-green-700 dark:text-green-400' },
  { value: 'UNAVAILABLE_PLANNED', label: 'Absent prévu', icon: Clock, color: 'bg-yellow-50 border-yellow-300 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-400' },
  { value: 'UNAVAILABLE_UNPLANNED', label: 'Absent imprévu', icon: XCircle, color: 'bg-red-50 border-red-300 text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400' },
];

const AVAILABILITY_REASONS = [
  { value: 'NONE', label: '—' },
  { value: 'LEAVE', label: 'Congés' },
  { value: 'SICK_LEAVE', label: 'Arrêt maladie' },
  { value: 'TRAINING', label: 'Formation' },
  { value: 'MEDICAL_VISIT', label: 'Visite médicale' },
  { value: 'ADMIN', label: 'Administratif' },
  { value: 'VEHICLE_ISSUE', label: 'Problème véhicule' },
  { value: 'OTHER', label: 'Autre' },
];

function AvailabilityCard({ driver, onUpdate }) {
  const { fmt } = useFormat();
  const currentStatus = driver?.availability_status || 'UNKNOWN';
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    status: currentStatus === 'UNKNOWN' ? 'AVAILABLE' : currentStatus,
    reason: driver?.availability_reason || 'NONE',
    note: driver?.availability_note || '',
    from: driver?.availability_from ? driver.availability_from.split('T')[0] : '',
    until: driver?.availability_until ? driver.availability_until.split('T')[0] : '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      await driverMonoAPI.updateAvailability({
        status: form.status,
        reason: form.status === 'AVAILABLE' ? 'NONE' : form.reason,
        note: form.note || null,
        from: form.from ? new Date(form.from).toISOString() : null,
        until: form.until ? new Date(form.until).toISOString() : null,
      });
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const currentInfo = AVAILABILITY_STATUSES.find(s => s.value === currentStatus);
  const StatusIcon = currentInfo?.icon || Clock;

  if (!editing) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900 dark:text-white">Disponibilité</h4>
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
          >
            Modifier
          </button>
        </div>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${currentInfo?.color || 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-400'}`}>
          <StatusIcon className="h-4 w-4" />
          <span className="text-sm font-medium">{currentInfo?.label || 'Non renseigné'}</span>
        </div>
        {driver?.availability_reason && driver.availability_reason !== 'NONE' && (
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
            Raison: {AVAILABILITY_REASONS.find(r => r.value === driver.availability_reason)?.label || driver.availability_reason}
          </p>
        )}
        {driver?.availability_note && (
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Note: {driver.availability_note}</p>
        )}
        {(driver?.availability_from || driver?.availability_until) && (
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
            {driver.availability_from && `Du ${fmt.date(driver.availability_from)}`}
            {driver.availability_until && ` au ${fmt.date(driver.availability_until)}`}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
      <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Modifier ma disponibilité</h4>

      <div className="space-y-3">
        <div className="flex gap-2">
          {AVAILABILITY_STATUSES.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setForm(prev => ({ ...prev, status: s.value }))}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${
                  form.status === s.value ? s.color + ' ring-2 ring-offset-1 ring-blue-400 dark:ring-offset-slate-800' : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-600'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {s.label}
              </button>
            );
          })}
        </div>

        {form.status !== 'AVAILABLE' && (
          <>
            <div>
              <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Raison</label>
              <select
                value={form.reason}
                onChange={e => setForm(prev => ({ ...prev, reason: e.target.value }))}
                className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-1.5 text-sm"
              >
                {AVAILABILITY_REASONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Du</label>
                <input
                  type="date"
                  value={form.from}
                  onChange={e => setForm(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Au</label>
                <input
                  type="date"
                  value={form.until}
                  onChange={e => setForm(prev => ({ ...prev, until: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-1.5 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Note (optionnel)</label>
              <input
                type="text"
                value={form.note}
                onChange={e => setForm(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Courte note..."
                maxLength={120}
                className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-1.5 text-sm placeholder:text-gray-400 dark:placeholder:text-slate-500"
              />
            </div>
          </>
        )}

        {saveError && (
          <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
            <AlertCircle className="h-3.5 w-3.5" />
            {saveError}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-blue-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-3 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DriverProfileSummary() {
  const { fmt } = useFormat();
  const { moneyScope } = useOrgCurrency();
  const [profile, setProfile] = useState(null);
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileRes, costsRes] = await Promise.all([
        driverMonoAPI.getProfile(),
        driverMonoAPI.getCosts()
      ]);
      setProfile(profileRes.data);
      setCosts(costsRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <p className="text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  const driver = profile?.driver;
  const site = profile?.site;

  const costItems = [
    {
      label: 'Carburant',
      icon: Fuel,
      count: costs?.fuel?.count || 0,
      total: costs?.fuel?.total || 0,
      color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30'
    },
    {
      label: 'Amendes',
      icon: AlertTriangle,
      count: costs?.fines?.count || 0,
      total: costs?.fines?.total || 0,
      color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30'
    },
    {
      label: 'Dépenses',
      icon: DollarSign,
      count: costs?.expenses?.count || 0,
      total: costs?.expenses?.total || 0,
      color: 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/30'
    },
    {
      label: 'Sinistres',
      icon: FileWarning,
      count: costs?.claims?.count || 0,
      total: costs?.claims?.total || 0,
      color: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/30'
    }
  ];

  return (
    <div className="space-y-4">
      {/* Profile Card */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <User className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {driver?.first_name} {driver?.last_name}
            </h3>
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
              driver?.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
            }`}>
              {driver?.status === 'active' ? 'Actif' : driver?.status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {driver?.email && (
            <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-slate-300">
              <Mail className="h-4 w-4 text-gray-400 dark:text-slate-500" />
              <span>{driver.email}</span>
            </div>
          )}
          {driver?.phone && (
            <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-slate-300">
              <PhoneLink phone={driver.phone} />
            </div>
          )}
          {driver?.employee_id && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-slate-400">Matricule: </span>
              <span className="font-mono text-gray-900 dark:text-slate-300">{driver.employee_id}</span>
            </div>
          )}
          {driver?.hire_date && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-slate-400">Depuis: </span>
              <span className="text-gray-900 dark:text-slate-300">{fmt.date(driver.hire_date)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Site Card */}
      {site && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h4 className="font-semibold text-gray-900 dark:text-white">Site d'affectation</h4>
          </div>
          <p className="font-medium text-gray-900 dark:text-white">{site.name}</p>
          {site.address && <p className="text-sm text-gray-500 dark:text-slate-400">{site.address}</p>}
          {site.city && <p className="text-sm text-gray-500 dark:text-slate-400">{site.city}</p>}
        </div>
      )}

      {/* Availability */}
      <AvailabilityCard driver={driver} onUpdate={loadData} />

      {/* Cost Summary */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Résumé des coûts</h4>
        <div className="grid grid-cols-2 gap-3">
          {costItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={idx} className="p-3 rounded-lg border border-gray-100 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm text-gray-600 dark:text-slate-400">{item.label}</span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{moneyScope(item.total)}</p>
                <p className="text-xs text-gray-400 dark:text-slate-500">{item.count} enregistrement{item.count !== 1 ? 's' : ''}</p>
              </div>
            );
          })}
        </div>

        {/* Grand Total */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600 dark:text-slate-400">Total général</span>
          <span className="text-xl font-bold text-gray-900 dark:text-white">
            {moneyScope(costs?.grand_total || 0)}
          </span>
        </div>
      </div>
    </div>
  );
}
